import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
    ArrowLeft,
    Edit,
    Calendar,
    MapPin,
    Briefcase,
    Users,
    Clock,
    CheckCircle,
    AlertCircle,
    Building,
    FileText,
    Loader2,
    Copy,
    User,
    Download
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate';
import jobService from '../../../api/services/jobService';
import { formatDate, formatCurrency } from '../../../utils/stringUtils';
import toast from 'react-hot-toast';

const ViewJob = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isExporting, setIsExporting] = useState(false);

    const { data: job, isLoading, error } = useQuery({
        queryKey: ['job', id],
        queryFn: async () => {
            const result = await jobService.getJobById(id);
            if (result.success) return result.data;
            throw new Error(result.error || 'Failed to fetch job details');
        },
        enabled: !!id
    });

    const handleCopyJobLink = () => {
        const jobLink = `${window.location.origin}/job-application/${job.jobId || job._id}`;
        navigator.clipboard.writeText(jobLink);
        toast.success("Job link copied!");
    };

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        const toastId = toast.loading('Preparing Excel export...');
        try {
            const result = await jobService.exportJobDetail(job.jobId || job._id || id, job.title || 'Job');
            if (result.success) {
                toast.success('Job exported successfully', { id: toastId });
            } else {
                toast.error(result.error || 'Failed to export job', { id: toastId });
            }
        } catch (err) {
            toast.error(err.message || 'Failed to export job', { id: toastId });
        } finally {
            setIsExporting(false);
        }
    };

    const getStatusColor = (status) => {
        if (!status) return "bg-gray-100 text-gray-800";
        switch (status.toLowerCase()) {
            case "active":
                return "bg-green-100 text-green-800";
            case "draft":
                return "bg-yellow-100 text-yellow-800";
            case "closed":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getPriorityColor = (priority) => {
        if (!priority) return "bg-gray-100 text-gray-800";
        switch (priority.toLowerCase()) {
            case "high":
                return "bg-red-100 text-red-800";
            case "medium":
                return "bg-yellow-100 text-yellow-800";
            case "low":
                return "bg-green-100 text-green-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const renderEmployeeDetails = (user, label) => {
        if (!user) return null;

        const isObject = typeof user === "object" && user !== null;
        const fullName = isObject
            ? user.fullName ||
            `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
            "System"
            : user;

        return (
            <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {label}
                </span>
                <div className="flex items-start gap-3">
                    <div className="bg-gray-50 p-2 rounded-lg border border-gray-100">
                        <User className="h-4 w-4 text-gray-600" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">{fullName}</span>
                        {isObject && (
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                                {user.employeeId && (
                                    <span className="flex items-center gap-1">
                                        <span className="font-semibold text-gray-400">ID:</span>{" "}
                                        {user.employeeId}
                                    </span>
                                )}
                                {user.role && (
                                    <span className="flex items-center gap-1">
                                        <span className="font-semibold text-gray-400">Role:</span>{" "}
                                        <span className="capitalize">
                                            {user.role.replace(/_/g, " ")}
                                        </span>
                                    </span>
                                )}
                                {user.department && (
                                    <span className="flex items-center gap-1">
                                        <span className="font-semibold text-gray-400">Dept:</span>{" "}
                                        {user.department}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-agrosaf-green" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Job</h2>
                <p className="text-gray-600 mb-4">{error.message}</p>
                <Button onClick={() => navigate(-1)} variant="secondary">
                    Go Back
                </Button>
            </div>
        );
    }

    if (!job) return null;

    return (
        <div className="space-y-6 mx-auto">

            {/* Navigation & Actions */}
            <div className="flex items-center justify-between mb-8">
                <div className='flex items-center'>
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-gray-600 hover:text-gray-900 transition-colors font-medium"
                    >
                        <ArrowLeft className="h-5 w-5 mr-2" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Job Details</h1>
                        <p className='text-gray-600 mt-1'>View job details and manage applications</p>
                    </div>
                </div>
                <div className='flex items-center space-x-3'>
                    <Button
                        variant="outline"
                        onClick={handleExport}
                        disabled={isExporting}
                        className="flex items-center"
                    >
                        {isExporting ? (
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        ) : (
                            <Download className="w-5 h-5 mr-2" />
                        )}
                        {isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleCopyJobLink}
                        className="flex items-center"
                    >
                        <Copy className="w-5 h-5 mr-2" />
                        Copy Link
                    </Button>
                    <PermissionGate permission="recruitment" action="update">
                        <Button
                            variant="primary"
                            onClick={() => navigate(`/edit-job/${id}`)}
                            className="flex items-center bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                        >
                            <Edit className="w-5 h-5 mr-2" />
                            Edit Job Post
                        </Button>
                    </PermissionGate>
                </div>
            </div>

            {/* Hero Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4 capitalize break-all">
                            {job.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4 text-gray-600">
                            <span className="flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-semibold border border-blue-100 break-words max-w-full">
                                <Building className="h-4 w-4 mr-2 flex-shrink-0" />
                                {job.department}
                            </span>
                            <span className="flex items-center bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full text-sm font-semibold border border-purple-100 break-words max-w-full">
                                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                                {job.location}
                            </span>
                            <span className="flex items-center bg-orange-50 text-orange-700 px-3 py-1.5 rounded-full text-sm font-semibold border border-orange-100 break-words max-w-full">
                                <Briefcase className="h-4 w-4 mr-2 flex-shrink-0" />
                                {job.type === 'full_time' ? 'Full-time' : job.type === 'part_time' ? 'Part-time' : job.type}
                            </span>
                            <span className="flex items-center bg-teal-50 text-teal-700 px-3 py-1.5 rounded-full text-sm font-semibold border border-teal-100 break-words max-w-full capitalize">
                                <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                                Gender: {job.preferredGender || 'Any'}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <div className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center justify-center border ${getStatusColor(job.status)}`}>
                            <div className="w-2 h-2 rounded-full bg-current mr-2 animate-pulse" />
                            {job.status.toUpperCase()}
                        </div>
                        <div className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center justify-center border ${getPriorityColor(job.priority)}`}>
                            {job.priority.toUpperCase()} PRIORITY
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Description */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <FileText className="h-6 w-6 mr-3 text-purple-600" />
                            Job Description
                        </h2>
                        <div className="prose prose-gray max-w-none first-letter:capitalize">
                            <div
                                className="text-gray-700 leading-relaxed space-y-4"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description || 'No description available') }}
                            />
                        </div>
                    </section>

                    {/* Requirements */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <AlertCircle className="h-6 w-6 mr-3 text-blue-600" />
                            Requirements
                        </h2>
                        {job.requirements && job.requirements.length > 0 ? (
                            <ul className="space-y-4">
                                {job.requirements.map((req, index) => (
                                    <li key={index} className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                                        <span className="text-gray-700 leading-relaxed break-all capitalize">{req}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500 italic">No specific requirements mentioned.</p>
                        )}
                    </section>

                    {/* Skills */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <Users className="h-6 w-6 mr-3 text-green-600" />
                            Skills Required
                        </h2>
                        {job.skills && job.skills.length > 0 ? (
                            <ul className="space-y-4">
                                {job.skills.map((skill, index) => (
                                    <li key={index} className="flex items-start">
                                        <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                                        <span className="text-gray-700 leading-relaxed break-all capitalize">{skill}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500 italic">No specific skills mentioned.</p>
                        )}
                    </section>
                </div>

                {/* Sidebar Info */}
                <div className="lg:col-span-1 space-y-8">
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-6 border-b pb-4">Job Overview</h3>
                        <div className="space-y-6">
                            <div className="flex items-start">
                                <div className="bg-blue-50 p-2 rounded-lg mr-4">
                                    <Briefcase className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Experience</p>
                                    <p className="font-semibold text-gray-900">
                                        {job.experience?.name || (job.experience ? (job.experience.min === 10 && (!job.experience.max || job.experience.max === 0) ? '10+ years' : `${job.experience.min}-${job.experience.max} years`) : 'Not specified')}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="bg-teal-50 p-2 rounded-lg mr-4">
                                    <Users className="h-5 w-5 text-teal-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Preferred Gender</p>
                                    <p className="font-semibold text-gray-900 capitalize">
                                        {job.preferredGender || 'Any'}
                                    </p>
                                </div>
                            </div>


                            <div className="flex items-start">
                                <div className="bg-green-50 p-2 rounded-lg mr-4">
                                    <span className="text-green-600 font-bold text-lg">₹</span>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Offered Salary</p>
                                    <p className="font-semibold text-gray-900">
                                        {job.salary ? `${formatCurrency(job.salary.min)} - ${formatCurrency(job.salary.max)}` : 'Competitive'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="bg-purple-50 p-2 rounded-lg mr-4">
                                    <Calendar className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Date Posted</p>
                                    <p className="font-semibold text-gray-900">
                                        {job.postedDate ? formatDate(job.postedDate, { includeTime: false }) : 'Recently'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="bg-red-50 p-2 rounded-lg mr-4">
                                    <Clock className="h-5 w-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Deadline</p>
                                    <p className="font-semibold text-gray-900">
                                        {job.deadline ? formatDate(job.deadline, { includeTime: false }) : 'Ongoing'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="bg-gray-50 p-2 rounded-lg mr-4">
                                    <Users className="h-5 w-5 text-gray-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Applications</p>
                                    <p className="font-semibold text-gray-900">{job.applicationsCount || 0} received</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Record Information Section */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-6 border-b pb-4">
                            Record Information
                        </h3>
                        <div className="space-y-6">
                            {renderEmployeeDetails(job.createdBy, "Created By")}

                            <div className="flex items-start">
                                <div className="bg-blue-50 p-2 rounded-lg mr-4">
                                    <Calendar className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Created On</p>
                                    <p className="font-semibold text-gray-900">
                                        {formatDate(job.createdAt, { includeTime: true })}
                                    </p>
                                </div>
                            </div>

                            <div className="border-t border-gray-50 pt-6">
                                {renderEmployeeDetails(job.updatedBy, "Last Updated By")}
                            </div>

                            <div className="flex items-start">
                                <div className="bg-orange-50 p-2 rounded-lg mr-4">
                                    <Clock className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Last Updated On</p>
                                    <p className="font-semibold text-gray-900">
                                        {formatDate(job.updatedAt, { includeTime: true })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ViewJob;
